import { Capacitor, registerPlugin } from "@capacitor/core";
import { Directory, Encoding, Filesystem } from "@capacitor/filesystem";
import { nevisignFetch } from "@nevisign/reader-core";

type ShareFilePlugin = {
  share(options: {
    path?: string;
    uri?: string;
    title?: string;
    dialogTitle?: string;
    mimeType?: string;
    text?: string;
  }): Promise<{ ok: boolean; uri?: string }>;
};

const ShareFile = registerPlugin<ShareFilePlugin>("ShareFile");

export type LogLevel = "debug" | "info" | "warn" | "error" | "network";

export type LogEntry = {
  id: string;
  ts: string;
  level: LogLevel;
  tag: string;
  message: string;
  detail?: string;
};

const STORAGE_KEY = "nr-app-logs";
const MAX_ENTRIES = 400;
const DEDUPE_WINDOW_MS = 2500;
const listeners = new Set<() => void>();

/** Native console methods — never call patched console from inside the logger. */
const nativeConsole = {
  log: console.log.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
};

let buffer: LogEntry[] = loadPersisted();
let reentrant = false;
const recentKeys = new Map<string, number>();

function loadPersisted(): LogEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as LogEntry[];
    return Array.isArray(parsed) ? parsed.slice(-MAX_ENTRIES) : [];
  } catch {
    return [];
  }
}

function persist(): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(buffer.slice(-MAX_ENTRIES))
    );
  } catch {
    /* quota */
  }
}

function notify(): void {
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      /* ignore */
    }
  }
}

export function subscribeLogs(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getLogs(): LogEntry[] {
  return buffer.slice();
}

export function clearLogs(): void {
  buffer = [];
  persist();
  notify();
}

/** Capacitor bridge / missing-plugin noise that previously caused log storms. */
function isNoiseMessage(message: string): boolean {
  const m = message.toLowerCase();
  if (!m) return true;
  if (m.includes("unimplemented")) return true;
  if (m.includes("not implemented")) return true;
  if (m.includes('"code":"unimplemented"')) return true;
  if (m.includes("plugin_not_implemented")) return true;
  // Capacitor Filesystem.mkdir when folder already exists
  if (m.includes("os-plug-file-0010")) return true;
  if (m.includes("already exists, cannot be overwritten")) return true;
  return false;
}

function isDuplicate(key: string): boolean {
  const now = Date.now();
  const last = recentKeys.get(key);
  if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return true;
  recentKeys.set(key, now);
  if (recentKeys.size > 200) {
    for (const [k, ts] of recentKeys) {
      if (now - ts > DEDUPE_WINDOW_MS * 4) recentKeys.delete(k);
    }
  }
  return false;
}

export function appendLog(
  level: LogLevel,
  tag: string,
  message: string,
  detail?: unknown
): void {
  if (reentrant) return;
  if (isNoiseMessage(message)) return;

  let detailStr: string | undefined;
  if (detail !== undefined) {
    try {
      detailStr =
        typeof detail === "string" ? detail : JSON.stringify(detail, null, 2);
    } catch {
      detailStr = String(detail);
    }
  }
  if (detailStr && isNoiseMessage(detailStr)) return;

  const dedupeKey = `${level}|${tag}|${message.slice(0, 240)}`;
  if (isDuplicate(dedupeKey)) return;

  reentrant = true;
  try {
    const entry: LogEntry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      ts: new Date().toISOString(),
      level,
      tag,
      message,
      detail: detailStr,
    };
    buffer = [...buffer.slice(-(MAX_ENTRIES - 1)), entry];
    persist();
    notify();

    // Echo via *native* console only — never the patched hooks.
    const line = `[nr-log] [${tag}] ${message}`;
    if (level === "error") nativeConsole.error(line, detail ?? "");
    else if (level === "warn") nativeConsole.warn(line, detail ?? "");
    else if (level === "network") nativeConsole.info(line, detail ?? "");
    else nativeConsole.log(line, detail ?? "");
  } finally {
    reentrant = false;
  }
}

export function exportLogsText(): string {
  return buffer
    .map((e) => {
      const head = `${e.ts} [${e.level}] [${e.tag}] ${e.message}`;
      return e.detail ? `${head}\n${e.detail}` : head;
    })
    .join("\n\n");
}

export type LogFileExport = {
  fileName: string;
  text: string;
  /** Capacitor file URI when written on native. */
  uri?: string;
  /** Relative path under Directory.Cache (for ShareFile native plugin). */
  path?: string;
  directory?: "CACHE" | "DOCUMENTS";
  mimeType?: string;
};

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunk = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function guessMime(fileName: string, fallback?: string): string {
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".pdf")) return "application/pdf";
  if (lower.endsWith(".docx")) {
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  }
  if (lower.endsWith(".doc")) return "application/msword";
  if (lower.endsWith(".txt")) return "text/plain";
  return fallback || "application/octet-stream";
}

/**
 * Write arbitrary UTF-8 text under app Cache (native) for FileProvider share.
 * Never rely on the user browsing Cache — always pair with shareLogFile / ShareFile.
 * On web, returns text for Blob/File share or download.
 */
export async function writeShareableTextFile(
  text: string,
  fileName: string
): Promise<LogFileExport> {
  const body = text || "(empty)";
  const path = `nevisign-share/${fileName}`;

  if (Capacitor.isNativePlatform()) {
    try {
      await Filesystem.mkdir({
        path: "nevisign-share",
        directory: Directory.Cache,
        recursive: true,
      }).catch(() => undefined);

      const written = await Filesystem.writeFile({
        path,
        data: body,
        directory: Directory.Cache,
        encoding: Encoding.UTF8,
      });
      let uri = written.uri;
      try {
        const resolved = await Filesystem.getUri({
          path,
          directory: Directory.Cache,
        });
        uri = resolved.uri || uri;
      } catch {
        /* keep writeFile uri */
      }
      appendLog("info", "share-file", `wrote ${fileName}`, { uri, path });
      return { fileName, text: body, uri, path, directory: "CACHE" };
    } catch (err) {
      try {
        const written = await Filesystem.writeFile({
          path: fileName,
          data: body,
          directory: Directory.Cache,
          encoding: Encoding.UTF8,
        });
        return {
          fileName,
          text: body,
          uri: written.uri,
          path: fileName,
          directory: "CACHE",
        };
      } catch {
        appendLog(
          "warn",
          "share-file",
          "filesystem write failed",
          err instanceof Error ? err.message : err
        );
      }
    }
  }

  return { fileName, text: body };
}

/**
 * Write logs to a real file (Cache on native, Blob download on web)
 * so the user can share the file itself.
 */
export async function writeLogsToFile(): Promise<LogFileExport> {
  const text = exportLogsText() || "(no logs)";
  const fileName = `nevisign-reader-logs-${Date.now()}.txt`;
  return writeShareableTextFile(text, fileName);
}

/**
 * Write binary document bytes under Cache for FileProvider share
 * (PDF / DOCX). Reuses the same ShareFile path as log sharing.
 */
export async function writeShareableBinaryFile(
  buffer: ArrayBuffer,
  fileName: string,
  mimeType?: string
): Promise<LogFileExport> {
  const safe =
    (fileName || "document").replace(/[/\\?%*:|"<>]/g, "_").slice(0, 120) ||
    "document";
  const path = `nevisign-share/${safe}`;
  const mime = guessMime(safe, mimeType);

  if (Capacitor.isNativePlatform()) {
    try {
      await Filesystem.mkdir({
        path: "nevisign-share",
        directory: Directory.Cache,
        recursive: true,
      }).catch(() => undefined);

      const written = await Filesystem.writeFile({
        path,
        data: arrayBufferToBase64(buffer),
        directory: Directory.Cache,
      });
      let uri = written.uri;
      try {
        const resolved = await Filesystem.getUri({
          path,
          directory: Directory.Cache,
        });
        uri = resolved.uri || uri;
      } catch {
        /* keep writeFile uri */
      }
      appendLog("info", "share-file", `wrote binary ${safe}`, {
        uri,
        path,
        bytes: buffer.byteLength,
        mime,
      });
      return {
        fileName: safe,
        text: "",
        uri,
        path,
        directory: "CACHE",
        mimeType: mime,
      };
    } catch (err) {
      appendLog(
        "warn",
        "share-file",
        "binary filesystem write failed",
        err instanceof Error ? err.message : err
      );
    }
  }

  return { fileName: safe, text: "", mimeType: mime };
}

/** Share a previously written text/log file via Android share sheet (real .txt). */
export async function shareLogFile(
  exported: LogFileExport,
  options?: { title?: string; dialogText?: string; mimeType?: string }
): Promise<void> {
  const { fileName, text, uri, path } = exported;
  const mimeType =
    options?.mimeType || exported.mimeType || guessMime(fileName, "text/plain");
  const title = options?.title ?? "Nevisign Logs";
  const dialogText =
    options?.dialogText ?? `لاگ نویساین — ${fileName}`;

  // Native: FileProvider ACTION_SEND — real attachment for WhatsApp etc.
  if (
    Capacitor.isNativePlatform() &&
    Capacitor.isPluginAvailable("ShareFile") &&
    (path || uri)
  ) {
    try {
      await ShareFile.share({
        path: path || undefined,
        uri: uri || undefined,
        title,
        dialogTitle: dialogText,
        mimeType,
        text: dialogText,
      });
      appendLog("info", "share-file", "native share sheet opened", {
        fileName,
        path,
        mimeType,
      });
      return;
    } catch (err) {
      appendLog(
        "warn",
        "share-file",
        "native ShareFile failed",
        err instanceof Error ? err.message : err
      );
    }
  }

  // Web / fallback: Web Share API with a real File blob
  try {
    const blob = new Blob([text], { type: `${mimeType};charset=utf-8` });
    const file = new File([blob], fileName, { type: mimeType });
    if (
      typeof navigator.canShare === "function" &&
      navigator.canShare({ files: [file] })
    ) {
      await navigator.share({
        files: [file],
        title,
        text: dialogText,
      });
      return;
    }
  } catch {
    /* fall through */
  }

  try {
    if (typeof navigator.share === "function") {
      await navigator.share({
        title,
        text: text.slice(0, 120_000),
      });
      return;
    }
  } catch {
    /* fall through */
  }

  try {
    await navigator.clipboard.writeText(text);
    alert("متن در کلیپ‌بورد کپی شد.");
    return;
  } catch {
    /* fall through */
  }

  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

/** Share an open PDF/DOCX via the system share sheet (real file attachment). */
export async function shareOpenDocument(opts: {
  buffer: ArrayBuffer;
  fileName: string;
  mimeType?: string;
}): Promise<void> {
  const { buffer, fileName, mimeType } = opts;
  if (!buffer || buffer.byteLength === 0) {
    throw new Error("فایل برای اشتراک در دسترس نیست.");
  }
  const exported = await writeShareableBinaryFile(buffer, fileName, mimeType);
  const mime = exported.mimeType || guessMime(fileName, mimeType);

  if (
    Capacitor.isNativePlatform() &&
    Capacitor.isPluginAvailable("ShareFile") &&
    (exported.path || exported.uri)
  ) {
    await ShareFile.share({
      path: exported.path || undefined,
      uri: exported.uri || undefined,
      title: fileName,
      dialogTitle: `اشتراک ${fileName}`,
      mimeType: mime,
      text: fileName,
    });
    appendLog("info", "share-file", "document share sheet opened", {
      fileName,
      path: exported.path,
      mime,
    });
    return;
  }

  // Web: File share or download
  const blob = new Blob([buffer], { type: mime });
  const file = new File([blob], fileName, { type: mime });
  try {
    if (
      typeof navigator.canShare === "function" &&
      navigator.canShare({ files: [file] })
    ) {
      await navigator.share({ files: [file], title: fileName });
      return;
    }
  } catch {
    /* fall through */
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

export type UploadLogsResult = {
  ok: boolean;
  id?: string;
  error?: string;
};

/**
 * Upload log / PDF report text to contract-ai:
 * POST {apiBase}/api/reader/logs/upload
 */
export async function uploadLogsToServer(
  apiBase: string,
  params: {
    kind: "app-logs" | "pdf-report";
    text: string;
    fileName?: string;
    versionCode?: number;
    versionName?: string;
  }
): Promise<UploadLogsResult> {
  if (!apiBase.trim()) {
    return { ok: false, error: "آدرس پایه API تنظیم نشده است." };
  }
  try {
    const res = await nevisignFetch(apiBase, "/api/reader/logs/upload", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        kind: params.kind,
        text: params.text,
        logs: params.text,
        fileName: params.fileName,
        versionCode: params.versionCode,
        versionName: params.versionName,
        appVersion: params.versionName,
        device: Capacitor.getPlatform(),
        deviceId: Capacitor.getPlatform(),
      }),
    });
    const json = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      id?: string;
      error?: string;
    };
    if (!res.ok) {
      return {
        ok: false,
        error: json.error || `آپلود ناموفق (HTTP ${res.status})`,
      };
    }
    appendLog("info", "upload", `uploaded ${params.kind}`, {
      id: json.id,
    });
    return { ok: true, id: json.id };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    appendLog("error", "upload", msg);
    return { ok: false, error: msg };
  }
}

let consoleHooked = false;
let fetchHooked = false;
let rejectionHooked = false;

/** Capture console + fetch failures into the ring buffer (once). */
export function installLogHooks(): void {
  if (typeof window === "undefined") return;

  if (!consoleHooked) {
    consoleHooked = true;
    const wrap =
      (level: LogLevel, original: (...args: unknown[]) => void) =>
      (...args: unknown[]) => {
        try {
          const message = args
            .map((a) =>
              typeof a === "string"
                ? a
                : a instanceof Error
                  ? a.message
                  : JSON.stringify(a)
            )
            .join(" ");
          // Skip our own echoes and Capacitor UNIMPLEMENTED storms.
          if (
            message &&
            !message.startsWith("[nr-log]") &&
            !isNoiseMessage(message)
          ) {
            appendLog(level, "console", message.slice(0, 2000));
          }
        } catch {
          /* ignore */
        }
        original(...args);
      };
    console.error = wrap("error", nativeConsole.error);
    console.warn = wrap("warn", nativeConsole.warn);
  }

  if (!fetchHooked) {
    fetchHooked = true;
    const nativeFetch = window.fetch.bind(window);
    window.fetch = async (input, init) => {
      const url =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.href
            : input.url;
      const method = (init?.method ?? "GET").toUpperCase();
      const started = Date.now();
      try {
        const res = await nativeFetch(input, init);
        const ms = Date.now() - started;
        if (!res.ok || url.includes("/api/")) {
          appendLog(
            res.ok ? "network" : "error",
            "fetch",
            `${method} ${url} → ${res.status} (${ms}ms)`
          );
        }
        return res;
      } catch (err) {
        appendLog(
          "error",
          "fetch",
          `${method} ${url} failed`,
          err instanceof Error ? err.message : err
        );
        throw err;
      }
    };
  }

  if (!rejectionHooked) {
    rejectionHooked = true;
    window.addEventListener("unhandledrejection", (ev) => {
      const msg =
        ev.reason instanceof Error ? ev.reason.message : String(ev.reason);
      if (isNoiseMessage(msg)) return;
      appendLog("error", "unhandled", msg);
    });
  }
}
